import { useState } from "react";
import { MessageSquare, Trash2, X } from "lucide-react";
import type { ChatSession } from "@racler/protocol";
import { groupChats, relativeTime } from "../lib/chatGroups.ts";
import { ConfirmDialog } from "./Dialog.tsx";
import { IconButton } from "./IconButton.tsx";

interface Props {
  sessions: ChatSession[];
  activeId: string | null;
  runningId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => Promise<void>;
  onNew: () => void;
  /** Omit in the standalone History panel, where visibility is controlled by the panel selector. */
  onClose?: () => void;
}

/** Chat history as a real list (grouped Today / Yesterday / Older) with New Chat and delete-with-confirmation. */
export function ChatHistory({ sessions, activeId, runningId, onSelect, onDelete, onNew, onClose }: Props) {
  const [pendingDelete, setPendingDelete] = useState<ChatSession | null>(null);
  const groups = groupChats(sessions);
  return (
    <div className="history" data-testid="chat-history">
      <div className="history-head">
        <button className="history-new" onClick={onNew} data-testid="history-new-chat">
          New chat
        </button>
        {onClose && <IconButton icon={X} label="Close history" onClick={onClose} />}
      </div>
      <div className="history-body">
        {groups.length === 0 && <div className="muted small history-empty">No chats yet.</div>}
        {groups.map((g) => (
          <section key={g.label}>
            <h4>{g.label}</h4>
            <ul>
              {g.items.map((s) => (
                <li key={s.id} className={s.id === activeId ? "active" : ""}>
                  <button
                    className="history-item"
                    onClick={() => onSelect(s.id)}
                    title={s.title}
                    data-testid="chat-item"
                  >
                    <MessageSquare size={13} aria-hidden />
                    <span className="history-title">{s.title}</span>
                    <span className="muted small">{relativeTime(s.updatedAt)}</span>
                  </button>
                  <IconButton
                    icon={Trash2}
                    label={s.id === runningId ? "Stop the response before deleting" : "Delete chat"}
                    disabled={s.id === runningId}
                    onClick={() => setPendingDelete(s)}
                    testId="chat-delete"
                    size={13}
                  />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      {pendingDelete && (
        <ConfirmDialog
          title="Delete chat?"
          danger
          confirmLabel="Delete"
          message={
            <p>
              Permanently delete <strong>{pendingDelete.title}</strong> and all of its messages? This cannot
              be undone.
            </p>
          }
          onClose={() => setPendingDelete(null)}
          onConfirm={() => onDelete(pendingDelete.id)}
        />
      )}
    </div>
  );
}
